// src/components/layout/AnnouncementTicker.jsx
//
// Thin scrolling announcement strip shown directly under the header.
// Messages are edited in src/config/app.config.js -> site.announcementTicker.
//
// Accessibility notes:
//  - WCAG 2.2.2 requires a way to pause auto-moving content, so the strip has a
//    pause/play control and also pauses on hover and on keyboard focus.
//  - The duplicated track (needed for a seamless loop) is aria-hidden so screen
//    readers read each message exactly once.
//  - prefers-reduced-motion is honoured globally in globals.css; the strip also
//    checks it in JS so it starts paused rather than starting and freezing.

"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { Pause, Play, Megaphone } from "lucide-react";
import APP_CONFIG from "@/config/app.config";

const isLive = (message) => {
  if (!message?.text) return false;
  // Optional scheduling: hide outside startsAt / endsAt when provided.
  const now = Date.now();
  if (message.startsAt && now < new Date(message.startsAt).getTime()) return false;
  if (message.endsAt && now > new Date(message.endsAt).getTime()) return false;
  return true;
};

const MessageItem = ({ message }) => {
  const content = (
    <span className="inline-flex items-center gap-2">
      {message.emoji ? (
        <span aria-hidden="true">{message.emoji}</span>
      ) : null}
      <span>{message.text}</span>
    </span>
  );

  if (message.href) {
    return (
      <Link
        href={message.href}
        className="inline-flex items-center underline-offset-4 transition-colors hover:underline focus-visible:underline"
      >
        {content}
      </Link>
    );
  }
  return content;
};

const Track = ({ messages, ariaHidden = false }) => (
  <div
    aria-hidden={ariaHidden ? "true" : undefined}
    className="flex shrink-0 items-center gap-10 pr-10"
  >
    {messages.map((message, index) => (
      <span
        key={`${message.text}-${index}`}
        className="inline-flex items-center gap-10 whitespace-nowrap"
      >
        <MessageItem message={message} />
        <span aria-hidden="true" className="text-amber-500/60">
          •
        </span>
      </span>
    ))}
  </div>
);

export const AnnouncementTicker = () => {
  const config = APP_CONFIG.site?.announcementTicker || {};
  const [paused, setPaused] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    if (typeof window === "undefined" || !window.matchMedia) return;
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (query.matches) setPaused(true);
    const onChange = (event) => setPaused(event.matches);
    query.addEventListener?.("change", onChange);
    return () => query.removeEventListener?.("change", onChange);
  }, []);

  if (config.enabled === false) return null;

  const messages = (Array.isArray(config.messages) ? config.messages : []).filter(isLive);
  if (messages.length === 0) return null;

  const speedSeconds = Number(config.speedSeconds) > 0 ? Number(config.speedSeconds) : 38;

  return (
    <aside
      aria-label="Store announcements"
      className="relative w-full overflow-hidden border-b border-amber-500/20 bg-[#12121a] text-white"
    >
      <div className="flex h-9 items-center">
        {/* Leading badge */}
        <div className="hidden shrink-0 items-center gap-1.5 bg-amber-500 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-zinc-950 sm:flex">
          <Megaphone className="h-3 w-3" aria-hidden="true" />
          News
        </div>

        {/* Scrolling area */}
        <div
          className="group relative flex-1 overflow-hidden"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocusCapture={() => setPaused(true)}
          onBlurCapture={() => setPaused(false)}
        >
          <div
            className="flex w-max items-center text-xs font-medium text-zinc-200 sm:text-[13px]"
            // Longhand properties only: mixing the `animation` shorthand with animationPlayState
            // makes React warn about conflicting styles on every pause / resume.
            style={{
              animationName: mounted ? "marquee" : undefined,
              animationDuration: `${speedSeconds}s`,
              animationTimingFunction: "linear",
              animationIterationCount: "infinite",
              animationPlayState: paused ? "paused" : "running",
            }}
          >
            <Track messages={messages} />
            <Track messages={messages} ariaHidden />
          </div>

          {/* Edge fades */}
          <div className="pointer-events-none absolute inset-y-0 left-0 w-8 bg-linear-to-r from-[#12121a] to-transparent" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-linear-to-l from-[#12121a] to-transparent" />
        </div>

        {/* Pause / play (WCAG 2.2.2) */}
        <button
          type="button"
          onClick={() => setPaused((prev) => !prev)}
          aria-label={paused ? "Resume announcements" : "Pause announcements"}
          className="shrink-0 px-3 text-zinc-400 transition-colors hover:text-amber-400 focus-visible:text-amber-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/70"
        >
          {paused ? (
            <Play className="h-3.5 w-3.5" aria-hidden="true" />
          ) : (
            <Pause className="h-3.5 w-3.5" aria-hidden="true" />
          )}
        </button>
      </div>
    </aside>
  );
};

export default AnnouncementTicker;
